import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const DURATION = 5000;
const FADE = 500;
const REPEAT_MS = 4 * 60 * 1000;
const PUBLIC_PATHS = ["/", "/portal"];

// Anyone can trigger a mow: window.dispatchEvent(new Event("zz-mow"))
export const triggerMow = () => window.dispatchEvent(new Event("zz-mow"));

// Rows of chevron zig-zags; mower turns around off-screen between rows
const buildPath = (w, h) => {
  const mobile = w < 640;
  const rows = 3;
  const period = mobile ? 150 : 280;
  const amp = mobile ? 28 : 48;
  const pad = 140;
  const pts = [];
  for (let r = 0; r < rows; r++) {
    const y = h * (0.2 + (0.6 * r) / (rows - 1));
    const ltr = r % 2 === 0;
    const half = period / 2;
    const n = Math.ceil((w + pad * 2) / half);
    for (let i = 0; i <= n; i++) {
      const x = ltr ? -pad + i * half : w + pad - i * half;
      pts.push({ x, y: y + (i % 2 === 0 ? amp : -amp) });
    }
  }
  const segs = [];
  let total = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    segs.push({ a, b, len, start: total });
    total += len;
  }
  const d = pts.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  return { segs, total, d, band: mobile ? 46 : 74 };
};

const pointAt = ({ segs }, s) => {
  const seg = segs.find((g) => s <= g.start + g.len) || segs[segs.length - 1];
  const t = Math.min(1, Math.max(0, (s - seg.start) / seg.len));
  return {
    x: seg.a.x + (seg.b.x - seg.a.x) * t,
    y: seg.a.y + (seg.b.y - seg.a.y) * t,
    right: seg.b.x >= seg.a.x,
    slope: Math.atan2(seg.b.y - seg.a.y, Math.abs(seg.b.x - seg.a.x)),
  };
};

const MOWER_W = typeof window !== "undefined" && window.innerWidth < 640 ? 120 : 220;
const MOWER_H = Math.round(MOWER_W * 257 / 420);

const RealMower = () => (
  <img src="/zz-mower.png" alt="" width={MOWER_W} height={MOWER_H} draggable="false" className="select-none" data-testid="mower-image" />
);

const MowRun = ({ onDone }) => {
  const mowerRef = useRef(null);
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [path] = useState(() => buildPath(window.innerWidth, window.innerHeight));
  const [fading, setFading] = useState(false);

  useEffect(() => {
    let raf;
    let tilt = 0;
    const t0 = performance.now();
    const runTime = DURATION - FADE;
    const step = (now) => {
      const k = Math.min(1, (now - t0) / runTime);
      const s = path.total * k;
      const p = pointAt(path, s);
      const offset = `${Math.max(0, path.total - s)}`;
      outerRef.current?.setAttribute("stroke-dashoffset", offset);
      innerRef.current?.setAttribute("stroke-dashoffset", offset);
      tilt += ((p.slope * 180) / Math.PI * 0.6 - tilt) * 0.2;
      if (mowerRef.current) {
        mowerRef.current.style.transform = `translate(${p.x - MOWER_W * 0.62}px, ${p.y - MOWER_H * 0.72}px) scaleX(${p.right ? 1 : -1}) rotate(${tilt}deg)`;
      }
      if (k < 1) raf = requestAnimationFrame(step);
      else setFading(true);
    };
    raf = requestAnimationFrame(step);
    const done = setTimeout(onDone, DURATION);
    return () => { cancelAnimationFrame(raf); clearTimeout(done); };
  }, [path, onDone]);

  return (
    <div data-testid="mower-overlay" aria-hidden="true"
      className={`fixed inset-0 z-[9999] pointer-events-none overflow-hidden transition-opacity duration-500 ${fading ? "opacity-0" : "opacity-100"}`}
      style={{ background: "transparent" }}>
      <svg className="absolute inset-0 w-full h-full" data-testid="mower-zigzag-trail">
        <defs>
          <pattern id="zz-stripes" width="8" height="8" patternUnits="userSpaceOnUse">
            <rect width="8" height="8" fill="#C8F031" />
            <rect width="8" height="3" fill="#A3D916" />
          </pattern>
        </defs>
        <path ref={outerRef} d={path.d} fill="none" stroke="url(#zz-stripes)" strokeWidth={path.band}
          strokeLinejoin="miter" strokeMiterlimit="10" strokeDasharray={path.total} strokeDashoffset={path.total} opacity="0.92" />
        <path ref={innerRef} d={path.d} fill="none" stroke="#2DB52D" strokeWidth={path.band * 0.55}
          strokeLinejoin="miter" strokeMiterlimit="10" strokeDasharray={path.total} strokeDashoffset={path.total} />
      </svg>
      <div ref={mowerRef} className="absolute left-0 top-0 will-change-transform" style={{ transform: "translate(-300px,-300px)" }}>
        <div className="zz-bob" style={{ filter: "drop-shadow(0 6px 8px rgba(0,0,0,0.65))" }}><RealMower /></div>
      </div>
    </div>
  );
};

export const MowerOverlay = () => {
  const { pathname } = useLocation();
  const [runId, setRunId] = useState(0);
  const running = useRef(false);

  const start = useCallback(() => {
    if (running.current || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    running.current = true;
    setRunId((n) => n + 1);
  }, []);

  const finish = useCallback(() => { running.current = false; setRunId(0); }, []);

  useEffect(() => {
    new Image().src = "/zz-mower.png";
    window.addEventListener("zz-mow", start);
    return () => window.removeEventListener("zz-mow", start);
  }, [start]);

  // On the public website: once when first opened, then every few minutes
  useEffect(() => {
    if (!PUBLIC_PATHS.includes(pathname)) return;
    let first;
    if (pathname === "/" && !sessionStorage.getItem("zz_mowed")) {
      first = setTimeout(() => { sessionStorage.setItem("zz_mowed", "1"); start(); }, 900);
    }
    const every = setInterval(() => { if (!document.hidden) start(); }, REPEAT_MS);
    return () => { clearTimeout(first); clearInterval(every); };
  }, [pathname, start]);

  return runId ? <MowRun key={runId} onDone={finish} /> : null;
};
